import type { Effect } from 'effector'
import type { SyntheticEvent } from 'react'
import type { IPinLockPopupProps } from './popup/model'

import { attach, createEvent, restore, sample } from 'effector'
import { register } from '~/core/repository'
import { model as router } from '~/core/router'
import { addQuery } from '~/shared/routes'
import { $isLockedFn, $unlockedId, unlockId } from './content'
import {
  emptyPinLockState,
  openPinLockPopup,
  unlockContent,
} from './popup/model'

type InterceptParams = {
  id?: number
  locked?: boolean
  name?: string
  categoryName?: string
  '<event>': SyntheticEvent
}

type InterceptResult = {
  id?: number
  locked?: boolean
  name?: string
  categoryName?: string
  href?: string | null
}

/**
 * Event to intercept content interactions that may require PIN lock validation
 */
export const intercept = createEvent<InterceptParams>() //

/**
 * Effect to handle the interception logic, checking if content is locked
 * and returning the appropriate result or throwing an error to trigger the popup
 */
const interceptFx = attach({
  source: $isLockedFn,
  effect(isLocked, { '<event>': event, ...content }: InterceptParams) {
    const locked = isLocked(content)
    const prevented = event.defaultPrevented
    const target = event.target

    const { id, name, categoryName } = content

    // prettier-ignore
    const link = target instanceof HTMLElement ? (target instanceof HTMLAnchorElement ? target : target.closest('a')) : null
    const foundHref = link?.getAttribute('href')
    const href = addQuery(foundHref, { categoryName })

    //
    // if content is locked
    //

    if (locked) {
      // if event is not prevented, we need to prevent default action,
      // to stop the browser from navigating to the link
      if (!prevented) {
        event.preventDefault()
        event.stopPropagation()
      }

      // throw in order to branch the flow to the popup,
      // include the content data and the href to navigate to after successful unlock
      throw {
        id,
        name,
        categoryName,
        locked,
        href,
      }
    }

    //
    // if content is not locked
    //

    // if event is not prevented - just return nothing,
    // so the browser will navigate to the link as usual
    if (!prevented) {
      return
    }

    // otherwise return the content data with the href to navigate to,
    // in order to navigate to the link manually
    return {
      id,
      name,
      categoryName,
      locked,
      href,
    }
  },
}) as Effect<InterceptParams, InterceptResult | undefined, InterceptResult>

sample({
  clock: intercept,
  target: interceptFx,
})

//
// Opened flow branch
//

sample({
  clock: interceptFx.doneData,
  filter: (result) => typeof result?.href === 'string',
  fn: (result) => result!.href!,
  target: router.navigatePush,
})

//
// Locked flow branch
//

sample({
  clock: interceptFx.failData,
  filter: (result) => typeof result?.href === 'string',
  fn: ({ id, name, locked }) =>
    ({
      ...emptyPinLockState,
      id,
      name,
      locked,
    }) as IPinLockPopupProps,
  target: openPinLockPopup,
})

sample({
  clock: unlockId,
  source: {
    unlockedId: $unlockedId,
    result: restore(interceptFx.failData, null).reset([
      interceptFx,
      unlockContent,
    ]),
  },
  filter: ({ result, unlockedId }) =>
    typeof result?.href === 'string' && result.id === unlockedId,
  fn: ({ result }) => result!.href!,
  target: router.navigatePush,
})

//
//
//

register(intercept, 'features/pin-lock/intercept')
